import moment from 'moment';
import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { appOverride } from '../../../util/AppUtils';
import { Timer as ElapsedTimer } from '../../../util/Timer';
import { BodyXS } from '../../styles/Typography';

type CountdownTimerProps = {
  /** Counts down from this value of seconds */
  initSeconds: number;
  message: string;
  onZero?: () => void;
  className?: string;
};

/** A card with a timer counting down from `initSeconds` to zero */
export const CountdownTimer: FunctionComponent<CountdownTimerProps> = (props) => {
  const secondsRemaining = useCountdownTimer(props.initSeconds, props.onZero);

  return (
    <TimerContainer className={props.className} data-test="countdownTimer">
      <Paragraph>{props.message}</Paragraph>
      <TimerLabel>Time remaining:</TimerLabel>
      <Timer>{formatSecondsRemainingAsTime(secondsRemaining)}</Timer>
    </TimerContainer>
  );
};

/** Seconds left, counting down from `initSeconds`; `onZero` runs once when it reaches zero. */
export function useCountdownTimer(initSeconds: number, onZero?: () => void): number {
  const [secondsRemaining, setSecondsRemaining] = useState(initSeconds);

  useEffect(
    function handleEndOfTimer() {
      if (secondsRemaining === 0) {
        onZero?.();
      }
    },
    [secondsRemaining]
  );

  useEffect(function countDownByElapsedTime() {
    // Browsers throttle or pause intervals in background tabs, so counting ticks would fall behind.
    const timer = new ElapsedTimer();
    const interval = setInterval(() => {
      // Clamped both ways because the device clock can be moved back as well as forward.
      const elapsed = Math.max(0, Math.floor(timer.secondsElapsed()));
      const seconds = Math.max(0, initSeconds - elapsed);
      setSecondsRemaining(seconds);
      if (seconds === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return secondsRemaining;
}

/** Formats seconds to a time string e.g. `325` secs becomes `'5:25'` */
export function formatSecondsRemainingAsTime(seconds: number): string {
  return moment.utc(seconds * 1000).format('mm:ss');
}

const TimerContainer = styled.div`
  margin: 24px -16px;
  background-color: ${appOverride({
    nerva: 'rgba(255, 255, 255, 0.1)',
    default: Color.subscription.surface,
  })};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing._16px};
  text-align: center;
  color: ${Color.content.default};
`;

const Paragraph = styled(BodyXS)`
  margin: ${({ theme }) => `0 0 ${theme.spacing._12px}`};
`;

const TimerLabel = styled(BodyXS)`
  margin: 0;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const Timer = styled(BodyXS)`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
`;
